/**
 * Pending-sync processor for patient writes (#1739).
 *
 * Owns one patient's durable FIFO queue: hydrate from storage, apply
 * answers locally, deliver them one at a time with the write's stable
 * Idempotency-Key, back off on transient failures, park permanent
 * failures for recovery. Framework-light on purpose — storage, transport,
 * cache application, and clocks are injected so the whole lifecycle is
 * unit-testable in plain node.
 */

import {
  EMPTY_QUEUE,
  backoffMs,
  dismissFailed,
  enqueueWriteReplacingFailed,
  failHead,
  headWrite,
  newerWritesSupersede,
  parseQueueState,
  recordAttempt,
  resolveHead,
  resolveFailed,
  retryFailed,
  serializeQueueState,
  localCleanupForFailedReplacement,
  countPatientWriteSections,
  patientWriteSection,
  replacementSupersedesFailed,
  type FailedPatientWrite,
  type PatientWriteSectionCounts,
  type PatientWriteQueueState,
  type PendingPatientWrite,
} from "./patientWriteQueue";
import {
  preparePatientWriteBodyForDelivery,
  reconcilePatientWriteBody,
} from "./mergePatientUpdate";

export type SendOutcome =
  | { kind: "delivered"; serverPatient?: unknown }
  | { kind: "transient"; message: string }
  | {
      kind: "permanent";
      reason: FailedPatientWrite["reason"];
      message: string;
    };

export type PatientSyncDeps = {
  loadRaw: () => Promise<string | null>;
  saveRaw: (raw: string) => Promise<void>;
  /** Deliver one write. Must classify its own failure — never throw. */
  sendWrite: (
    write: PendingPatientWrite,
    signal: AbortSignal,
  ) => Promise<SendOutcome>;
  /** Per-attempt liveness bound. The stable write id makes timeout replay safe. */
  deliveryTimeoutMs?: number;
  /** Apply a captured answer to the local cache (optimistic merge). */
  applyLocal: (body: Record<string, unknown>) => void;
  /** Replace the cache with the authoritative response before re-overlaying. */
  applyServer?: (patient: unknown) => void;
  /** Called when the queue fully drains — reconcile from the server. */
  onDrained: () => void;
  now: () => string;
  randomId: () => string;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
};

export const PATIENT_SYNC_DELIVERY_TIMEOUT_MS = 15_000;

const TRANSIENT_SYNC_OUTCOME: SendOutcome = {
  kind: "transient",
  message: "Sync is temporarily unavailable.",
};

export type PatientSyncSnapshot = {
  hydrated: boolean;
  pending: number;
  /** Counts of retained/accepting writes by non-sensitive section metadata. */
  pendingSections: PatientWriteSectionCounts;
  /** Sanitized summaries only. Medical answers never leave the manager. */
  failed: FailedPatientWriteSummary[];
  /** Answers held in memory while durable storage is accepting them. */
  accepting: number;
  /** Number of captured answers not durably owned by storage yet. */
  undurable: number;
  /** Storage hydration or durable-acceptance most recently failed. */
  storageError: boolean;
  /** True while a delivery attempt is on the wire. */
  inFlight: boolean;
};

export type FailedPatientWriteSummary = Omit<
  FailedPatientWrite,
  "body" | "fingerprint"
> & {
  sectionKey?: ReturnType<typeof patientWriteSection>;
};

type AcceptingPatientWrite = {
  body: Record<string, unknown>;
  fingerprint: string;
  /** Optimistic-only removal of anonymous rows from the parked failed body. */
  localRecoveryCleanup?: Record<string, unknown>;
  /** Exact parked write removed in the same durable transition as this edit. */
  replaceFailedId?: string;
  accepted: Promise<void>;
  resolveAccepted: () => void;
};

function summarizeFailedWrite(
  write: FailedPatientWrite,
): FailedPatientWriteSummary {
  return {
    id: write.id,
    enqueuedAt: write.enqueuedAt,
    attempts: write.attempts,
    failedAt: write.failedAt,
    reason: write.reason,
    message: write.message,
    generation: write.generation,
    warningDismissed: write.warningDismissed,
    sectionKey: patientWriteSection(write.body),
  };
}

function fingerprintSerializedBody(serialized: string): string {
  // Persist an opaque logical identity rather than a second copy of sensitive
  // medical answers. Four differently seeded FNV lanes make accidental
  // collisions vanishingly unlikely while keeping capture synchronous.
  return [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35]
    .map((seed) => {
      let hash = seed;
      for (let index = 0; index < serialized.length; index += 1) {
        hash ^= serialized.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
      }
      return (hash >>> 0).toString(16).padStart(8, "0");
    })
    .join("");
}

function retainedWritesByGeneration(state: PatientWriteQueueState) {
  return [...state.failed, ...state.pending].sort(
    (first, second) => first.generation - second.generation,
  );
}

/**
 * Deduplicate a capture only within the acceptance re-entrancy window: the
 * most recent pending write (a double submit, or a flush retry of the same
 * capture). Matching EARLIER pending writes would collapse an A-B-A
 * resubmission into the first write and let the stale intervening answer
 * become the server's terminal state — an intervening different write must
 * always re-enqueue the final answer. Parked failures deliberately do NOT
 * dedup: resubmitting a parked answer is a fresh intent that must re-enqueue
 * and deliver (swallowing it against the parked copy would leave a stale
 * intervening answer terminal); enqueueWriteReplacingFailed then supersedes
 * the parked copy on full scope coverage or coexists with it on partial.
 */
function isDuplicateCapture(
  state: PatientWriteQueueState,
  fingerprint: string,
): boolean {
  return state.pending.at(-1)?.fingerprint === fingerprint;
}

export class PatientSyncManager {
  private state: PatientWriteQueueState = EMPTY_QUEUE;
  private hydratePromise: Promise<void> | null = null;
  private hydrated = false;
  private storageError = false;
  private stateMutation: Promise<void> = Promise.resolve();
  private accepting: AcceptingPatientWrite[] = [];
  private nextGeneration = 0;
  private flushingAcceptance = false;
  private processing = false;
  private inFlight = false;
  private activeRequestController: AbortController | null = null;
  private disposeRequested = false;
  private disposed = false;
  private kickRequested = false;
  private retryHandle: unknown = null;
  /** Retry intents deferred while a delivery attempt is on the wire. */
  private pendingRetries: {
    id: string;
    resolve: () => void;
    reject: (error: unknown) => void;
  }[] = [];
  private listeners = new Set<(snapshot: PatientSyncSnapshot) => void>();
  // Rebuilt only on notify() so useSyncExternalStore consumers get a
  // referentially stable snapshot between state changes.
  private currentSnapshot: PatientSyncSnapshot = {
    hydrated: false,
    pending: 0,
    pendingSections: {},
    failed: [],
    accepting: 0,
    undurable: 0,
    storageError: false,
    inFlight: false,
  };

  constructor(private readonly deps: PatientSyncDeps) {}

  snapshot(): PatientSyncSnapshot {
    return {
      hydrated: this.hydrated,
      pending: this.state.pending.length,
      pendingSections: countPatientWriteSections([
        ...this.state.pending,
        ...this.state.failed,
        ...this.accepting,
      ]),
      failed: this.state.failed.map(summarizeFailedWrite),
      accepting: this.accepting.length,
      undurable: this.accepting.length,
      storageError: this.storageError,
      inFlight: this.inFlight,
    };
  }

  /** Referentially stable between notifications — for useSyncExternalStore. */
  cachedSnapshot(): PatientSyncSnapshot {
    return this.currentSnapshot;
  }

  subscribe(listener: (snapshot: PatientSyncSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.currentSnapshot);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.currentSnapshot = this.snapshot();
    for (const listener of this.listeners) listener(this.currentSnapshot);
  }

  /**
   * Serialize every state transition and persist it before publishing the
   * in-memory state. This prevents two fast answers (or an enqueue racing a
   * delivery acknowledgement) from resolving AsyncStorage writes out of order
   * and resurrecting/dropping queue entries after restart.
   */
  private mutateState(
    transform: (state: PatientWriteQueueState) => PatientWriteQueueState,
  ): Promise<void> {
    const mutation = this.stateMutation.then(async () => {
      const next = transform(this.state);
      if (next === this.state) return;
      await this.deps.saveRaw(serializeQueueState(next));
      this.state = next;
      this.notify();
    });
    this.stateMutation = mutation.catch(() => {});
    return mutation;
  }

  /** Load the persisted queue, re-apply retained answers, then resume delivery. */
  async hydrate(): Promise<void> {
    await this.ensureHydrated();
    if (this.state.pending.length > 0) this.kick();
  }

  private async ensureHydrated(): Promise<void> {
    if (this.disposed) return;
    if (!this.hydratePromise) {
      this.hydratePromise = (async () => {
        const restored = parseQueueState(await this.deps.loadRaw());
        this.state = restored;
        this.nextGeneration = Math.max(
          0,
          ...[...restored.pending, ...restored.failed].map(
            (write) => write.generation,
          ),
        );
        this.hydrated = true;
        this.storageError = false;
        for (const write of retainedWritesByGeneration(restored)) {
          this.deps.applyLocal(write.body);
        }
        this.notify();
      })().catch((error) => {
        this.hydratePromise = null;
        this.hydrated = false;
        this.storageError = true;
        this.notify();
        throw error;
      });
    }
    await this.hydratePromise;
  }

  /**
   * Capture an answer immediately, apply it locally, then resolve only once
   * durable storage owns it. Concurrent duplicate submissions share the same
   * acceptance promise and UUID, so a double tap cannot create two logical
   * writes while the first storage operation is pending.
   */
  enqueue(body: Record<string, unknown>): Promise<void> {
    return this.capture(body);
  }

  private capture(
    body: Record<string, unknown>,
    replaceFailedId?: string,
    localRecoveryCleanup?: Record<string, unknown>,
  ): Promise<void> {
    // Match the JSON request body and detach it before the first await. A
    // caller can safely reuse/mutate its form object without changing a retry
    // under an idempotency key that was already captured.
    const serializedBody = JSON.stringify(body);
    const capturedBody = JSON.parse(serializedBody) as Record<string, unknown>;
    const fingerprint = fingerprintSerializedBody(serializedBody);
    // Share the in-flight acceptance only with the MOST RECENT capture. An
    // A-B-A resubmission must not resolve against the earlier identical
    // capture, or the intervening opposite answer would be delivered last.
    const latestAccepting = this.accepting.at(-1);
    const duplicate =
      latestAccepting?.fingerprint === fingerprint &&
      latestAccepting.replaceFailedId === replaceFailedId
        ? latestAccepting
        : undefined;
    if (duplicate) return duplicate.accepted;
    if (this.disposed || this.disposeRequested) {
      return Promise.reject(new Error("Patient sync manager is detached"));
    }

    let resolveAccepted = () => {};
    const accepted = new Promise<void>((resolve) => {
      resolveAccepted = resolve;
    });
    this.accepting.push({
      body: capturedBody,
      fingerprint,
      localRecoveryCleanup,
      replaceFailedId,
      accepted,
      resolveAccepted,
    });
    if (localRecoveryCleanup) {
      this.deps.applyLocal(localRecoveryCleanup);
    }
    this.deps.applyLocal(capturedBody);
    this.notify();
    void this.flushAcceptance();
    return accepted;
  }

  /**
   * Re-apply every locally retained answer on top of a fresh server snapshot.
   * Failed writes remain visible until the patient retries, replaces, or
   * explicitly dismisses them.
   */
  reapplyRetained(): void {
    for (const write of retainedWritesByGeneration(this.state)) {
      this.deps.applyLocal(write.body);
    }
    for (const entry of this.accepting) {
      if (entry.localRecoveryCleanup) {
        this.deps.applyLocal(entry.localRecoveryCleanup);
      }
      this.deps.applyLocal(entry.body);
    }
  }

  /** Kick delivery (connectivity restored, app foregrounded, …). */
  kick(): void {
    if (this.disposed) return;
    if (this.retryHandle !== null) {
      this.deps.clearTimer(this.retryHandle);
      this.retryHandle = null;
    }
    if (this.processing) {
      this.kickRequested = true;
    }
    void this.flushAcceptance();
    if (!this.disposeRequested) void this.process();
  }

  async dismissFailedWrite(id: string): Promise<void> {
    await this.mutateState((state) => dismissFailed(state, id));
    this.deps.onDrained();
  }

  /**
   * Restore a parked write to the FIFO. While the process loop is active —
   * a delivery attempt on the wire OR any point between two loop iterations —
   * the reinsertion is DEFERRED and applied only at the loop's flush points,
   * after the settled head's bookkeeping and before the next head is chosen.
   * Mutating pending mid-iteration could shift the queue under an
   * already-captured head: its acknowledgement/attempt bookkeeping would
   * no-op against pending[0], the head would be re-sent as a replay AFTER
   * the retried write, and the older parked value would end terminal on any
   * shared answer scope.
   */
  async retryFailedWrite(id: string): Promise<void> {
    if (
      (this.inFlight || this.processing) &&
      !this.disposed &&
      !this.disposeRequested
    ) {
      const deferred = new Promise<void>((resolve, reject) => {
        this.pendingRetries.push({ id, resolve, reject });
      });
      // Guarantee a flush point runs even if the loop is about to exit.
      this.kick();
      return deferred;
    }
    await this.applyRetry(id);
  }

  private async applyRetry(id: string): Promise<void> {
    await this.mutateState((state) => retryFailed(state, id));
    this.reapplyRetained();
    this.kick();
  }

  /** Apply retry intents queued while a delivery attempt was on the wire. */
  private async flushPendingRetries(): Promise<void> {
    while (this.pendingRetries.length > 0 && !this.inFlight) {
      const intent = this.pendingRetries.shift()!;
      try {
        await this.applyRetry(intent.id);
        intent.resolve();
      } catch (error) {
        intent.reject(error);
      }
    }
  }

  private rejectPendingRetries(): void {
    const intents = this.pendingRetries;
    this.pendingRetries = [];
    for (const intent of intents) {
      intent.reject(new Error("Patient sync manager is detached"));
    }
  }

  /**
   * Accept a corrected body durably before dropping the parked original.
   * This keeps at least one recoverable exact answer through storage failures.
   */
  async editFailedWrite(
    id: string,
    replacement: Record<string, unknown>,
  ): Promise<void> {
    const original = this.state.failed.find((write) => write.id === id);
    if (!original) {
      await this.capture(replacement);
      return;
    }
    const originalSection = patientWriteSection(original.body);
    const replacementSection = patientWriteSection(replacement);
    if (
      originalSection === undefined ||
      replacementSection !== originalSection
    ) {
      throw new Error("The corrected answer does not match this section");
    }
    const repeatsOriginal =
      original.fingerprint ===
      fingerprintSerializedBody(JSON.stringify(replacement));
    if (repeatsOriginal) {
      throw new Error("The corrected answer has not changed");
    }
    const replacesFailure = replacementSupersedesFailed(
      this.state,
      id,
      replacement,
    );
    const localRecoveryCleanup = replacesFailure
      ? localCleanupForFailedReplacement(original.body, replacement)
      : undefined;
    await this.capture(
      replacement,
      replacesFailure ? id : undefined,
      localRecoveryCleanup,
    );
  }

  dispose(): void {
    this.disposeRequested = true;
    this.activeRequestController?.abort();
    if (this.retryHandle !== null) {
      this.deps.clearTimer(this.retryHandle);
      this.retryHandle = null;
    }
    this.listeners.clear();
    if (this.accepting.length > 0) {
      void this.flushAcceptance();
      return;
    }
    this.finishDispose();
  }

  private finishDispose(): void {
    this.disposed = true;
    this.rejectPendingRetries();
    if (this.retryHandle !== null) {
      this.deps.clearTimer(this.retryHandle);
      this.retryHandle = null;
    }
  }

  private scheduleRetry(delay: number): void {
    if (
      this.disposed ||
      (this.disposeRequested && this.accepting.length === 0) ||
      this.retryHandle !== null
    ) {
      return;
    }
    this.retryHandle = this.deps.setTimer(() => {
      this.retryHandle = null;
      this.kick();
    }, delay);
  }

  /**
   * Bound every wire attempt independently of the transport implementation.
   * Aborting handles the real fetch promptly; racing the promise also keeps
   * FIFO live if a client adapter ignores AbortSignal. A response that arrives
   * after the deadline is ignored locally and the same idempotency key is
   * replayed, so a server commit followed by a lost/late response applies once.
   */
  private async sendWriteWithDeadline(
    write: PendingPatientWrite,
  ): Promise<SendOutcome> {
    const controller = new AbortController();
    this.activeRequestController = controller;
    const configuredTimeout = this.deps.deliveryTimeoutMs;
    const timeoutMs =
      typeof configuredTimeout === "number" &&
      Number.isFinite(configuredTimeout) &&
      configuredTimeout > 0
        ? configuredTimeout
        : PATIENT_SYNC_DELIVERY_TIMEOUT_MS;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const timeout = new Promise<SendOutcome>((resolve) => {
      timeoutHandle = setTimeout(() => {
        controller.abort();
        resolve(TRANSIENT_SYNC_OUTCOME);
      }, timeoutMs);
    });
    const transport = Promise.resolve()
      .then(() => this.deps.sendWrite(write, controller.signal))
      .catch(() => TRANSIENT_SYNC_OUTCOME);

    try {
      return await Promise.race([transport, timeout]);
    } finally {
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      if (this.activeRequestController === controller) {
        this.activeRequestController = null;
      }
    }
  }

  /**
   * Move volatile captures into the serialized durable queue. A failed
   * AsyncStorage write leaves the exact body in `accepting`, publishes a
   * truthful not-synced snapshot, and retries without resolving the caller.
   */
  private async flushAcceptance(): Promise<void> {
    if (this.flushingAcceptance || this.disposed) return;
    this.flushingAcceptance = true;
    let acceptedNewWrite = false;
    try {
      await this.ensureHydrated();
      while (this.accepting.length > 0 && !this.disposed) {
        const entry = this.accepting[0]!;
        if (
          entry.replaceFailedId === undefined &&
          isDuplicateCapture(this.state, entry.fingerprint)
        ) {
          this.accepting.shift();
          this.storageError = false;
          entry.resolveAccepted();
          this.notify();
          continue;
        }
        const write: PendingPatientWrite = {
          id: this.deps.randomId(),
          body: entry.body,
          fingerprint: entry.fingerprint,
          enqueuedAt: this.deps.now(),
          generation: this.nextGeneration + 1,
          attempts: 0,
        };
        let appendedWrite = false;
        await this.mutateState((state) => {
          const duplicate = isDuplicateCapture(state, entry.fingerprint);
          let next = state;
          if (!duplicate) {
            next = enqueueWriteReplacingFailed(state, write);
            appendedWrite = true;
          }
          if (
            entry.replaceFailedId !== undefined &&
            next.failed.some((failed) => failed.id === entry.replaceFailedId)
          ) {
            next = resolveFailed(next, entry.replaceFailedId);
          }
          return next;
        });
        acceptedNewWrite ||=
          appendedWrite || entry.replaceFailedId !== undefined;
        if (appendedWrite) {
          this.nextGeneration = Math.max(this.nextGeneration, write.generation);
        }
        this.accepting.shift();
        this.storageError = false;
        entry.resolveAccepted();
        this.notify();
      }
      if (this.disposeRequested) {
        this.finishDispose();
      } else if (acceptedNewWrite) {
        this.kick();
      }
    } catch {
      this.storageError = true;
      this.notify();
      this.scheduleRetry(1_000);
    } finally {
      this.flushingAcceptance = false;
    }
  }

  private async process(): Promise<void> {
    if (this.processing || this.disposed || this.disposeRequested) return;
    try {
      await this.ensureHydrated();
    } catch {
      this.scheduleRetry(1_000);
      return;
    }
    if (this.processing || this.disposed || this.disposeRequested) return;
    this.processing = true;
    let processedWrite = false;
    try {
      for (;;) {
        await this.stateMutation;
        if (this.disposed || this.disposeRequested) return;
        // Apply retry intents deferred during the previous send, BEFORE the
        // next head is chosen: the settled head has already been resolved,
        // parked, or attempt-counted, so a retried write can safely take the
        // front of the queue without displacing an in-flight delivery.
        await this.flushPendingRetries();
        const head = headWrite(this.state);
        if (!head) break;
        processedWrite = true;

        this.inFlight = true;
        this.notify();
        const outcome = await this.sendWriteWithDeadline({
          ...head,
          body: preparePatientWriteBodyForDelivery(head.body),
        });
        this.inFlight = false;
        if (this.disposed || this.disposeRequested) return;

        if (outcome.kind === "delivered") {
          await this.mutateState((state) => {
            const resolved = resolveHead(state, head.id);
            if (!outcome.serverPatient) return resolved;
            return {
              ...resolved,
              pending: resolved.pending.map((write) => ({
                ...write,
                body: reconcilePatientWriteBody(
                  write.body,
                  outcome.serverPatient,
                ),
              })),
              failed: resolved.failed.map((write) => ({
                ...write,
                body: reconcilePatientWriteBody(
                  write.body,
                  outcome.serverPatient,
                ),
              })),
            };
          });
          if (outcome.serverPatient) {
            this.accepting = this.accepting.map((entry) => {
              const body = reconcilePatientWriteBody(
                entry.body,
                outcome.serverPatient,
              );
              return { ...entry, body };
            });
            this.deps.applyServer?.(outcome.serverPatient);
            this.reapplyRetained();
          }
          continue;
        }

        if (outcome.kind === "permanent") {
          await this.mutateState((state) => {
            // Drop instead of park only when newer pending writes cover EVERY
            // answer scope of the failed head. A partial overlap keeps the
            // head parked so its non-overlapped answers stay recoverable.
            if (newerWritesSupersede(state, head)) {
              return resolveHead(state, head.id);
            }
            return failHead(
              state,
              head.id,
              outcome.reason,
              outcome.message,
              this.deps.now(),
            );
          });
          continue;
        }

        // Transient: count the attempt, schedule a backed-off retry, stop
        // processing — FIFO means nothing later may jump the queue.
        await this.mutateState((state) => recordAttempt(state, head.id));
        // The attempt is now recorded against the settled head, so a retry
        // deferred during this send can safely prepend (it kicks delivery).
        await this.flushPendingRetries();
        const delay = backoffMs(this.state.pending[0]?.attempts ?? 1);
        this.scheduleRetry(delay);
        return;
      }

      if (processedWrite) this.deps.onDrained();
    } catch {
      // A persistence/read failure must never drop a captured answer or produce
      // an unhandled app-shell rejection. Keep the last durable state and retry
      // the transition later; a delivered write safely replays with the same
      // idempotency key if its acknowledgement could not be persisted.
      this.scheduleRetry(backoffMs(this.state.pending[0]?.attempts ?? 1));
    } finally {
      this.processing = false;
      this.notify();
      if (this.kickRequested && !this.disposed && !this.disposeRequested) {
        this.kickRequested = false;
        if (this.retryHandle !== null) {
          this.deps.clearTimer(this.retryHandle);
          this.retryHandle = null;
        }
        this.kick();
      }
    }
  }
}
